import { Show, useObservable } from "@legendapp/state/react"
import { Pressable, View, Platform, Modal } from "react-native"
import Icon from "@react-native-vector-icons/material-design-icons"
import { trpc } from "utils/trpc"
import { useState } from "react"
import { List } from "./list"
import { useDebounce } from "use-debounce"
import { ar } from "i18n/ar"
import { Text, TextInput } from "./core"
import { sharedStyles } from "@src/utils"
import { Header } from "./Header"

type Props = {
  value: number[]
  onSelectionChange: (ids: number[]) => void
  className?: string
}

export function BrandPicker({ value, onSelectionChange, className }: Props) {
  const brands = trpc.brands.list.useQuery()
  const visible = useObservable(false)

  const _value =
    brands.data
      ?.filter((brand) => value.includes(brand.id))
      ?.map((brand) => brand.name)
      ?.join(", ") || undefined

  function handlePress() {
    visible.set(true)
  }

  function hide() {
    visible.set(false)
  }

  return (
    <>
      <Pressable className={`min-h-[45px] bg-gray-50 border border-gray-300 rounded px-4 justify-center active:opacity-70 ${className}`} onPress={handlePress}>
        <Text className="text-gray-500">{_value ?? ar.selectBrand}</Text>
      </Pressable>

      <Show if={visible}>
        <BrandPickModal value={value} onSelectionChange={onSelectionChange} hide={hide} />
      </Show>
    </>
  )
}

function BrandPickModal({ value, onSelectionChange, hide }: Props & { hide: () => void }) {
  const brands = trpc.brands.list.useQuery()
  const [searchText, setSearchText] = useState("")
  const [debouncedSearchText] = useDebounce(searchText, 500)

  const filteredBrands = brands.data?.filter((brand) => brand.name.toLowerCase().includes(debouncedSearchText.toLowerCase())) || []

  return (
    <Modal animationType="slide" presentationStyle="formSheet" allowSwipeDismissal onDismiss={hide} onRequestClose={hide}>
      <Header
        title={ar.brands}
        withoutInsets={Platform.OS === "ios"}
        right={
          <Pressable onPress={hide} className="px-2 active:opacity-70 h-full justify-center items-center">
            <Icon name="close" size={25} />
          </Pressable>
        }
      />

      <View className="w-full max-w-content mx-auto px-2 mb-2">
        <TextInput placeholder={ar.searchBrands} value={searchText} onChangeText={setSearchText} />
      </View>

      <List
        data={filteredBrands}
        keyExtractor={(item) => item.id.toString()}
        contentContainerStyle={sharedStyles.contentContainer}
        renderItem={({ item }) => {
          return (
            <Pressable
              className="py-3 px-4 border-b border-gray-300 active:opacity-70"
              onPress={() => {
                onSelectionChange([item.id])
                hide()
              }}
            >
              <Text className={`${value.includes(item.id) ? "text-blue-600" : ""}`}>{item.name}</Text>
            </Pressable>
          )
        }}
      />
    </Modal>
  )
}
